import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, input, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ELoadStatus } from '../../../state/state.interface';
import { ErrorMessagesComponent } from '../error-messages/error-messages.component';
import { InfoMessageComponent } from '../info-message/info-message.component';
import { IDropdownOption, MatDropdown } from '../mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../mat-dropdown/scw-mat-dropdown.interface';
import {
  IConfigurationBlock,
  IInternalFormValue,
  IItemCategoryShiftConfiguration,
  ILayer,
  IOptionSelection,
  IShiftsPayload,
  TStatusCategory,
} from './item-category-shift-configuration.interface';
import { ItemCategoryShiftConfigurationStore } from './item-category-shift-configuration.store';

@Component({
  imports: [
    CommonModule,
    MatDropdown,
    AsyncPipe,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    TranslatePipe,
    MatIcon,
    MatMiniFabButton,
    MatTooltip,
    MatButton,
    ErrorMessagesComponent,
    InfoMessageComponent,
  ],
  providers: [ItemCategoryShiftConfigurationStore],
  selector: 'app-item-category-shift-configuration',
  standalone: true,
  styleUrl: './item-category-shift-configuration.component.scss',
  templateUrl: './item-category-shift-configuration.component.html',
})
export class ItemCategoryShiftConfigurationComponent implements OnInit {
  @Input() public formControl!: FormControl<IItemCategoryShiftConfiguration[] | null>;
  public isFormSubmitted = input<boolean>(false);

  public readonly shifts$: Observable<IDropdownOption<number>[]> = this.store.shiftsDropdown$;
  public readonly itemCategories$: Observable<IDropdownOption<number>[]> = this.store.itemCategoryDropdown$;
  public readonly internalForm: FormGroup = new FormGroup({ configurations: new FormArray<FormGroup>([]) });

  public readonly shiftDropdownConfiguration: IDropdownSettings;
  public readonly itemCategoryDropdownConfiguration: IDropdownSettings;
  public readonly optionDropdownConfiguration: IDropdownSettings;
  public readonly redStatusCategoryDropdownConfiguration: IDropdownSettings;
  public readonly redStatusCategoryOptions: IDropdownOption<TStatusCategory>[];
  public options: IOptionSelection[];

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor(private readonly store: ItemCategoryShiftConfigurationStore) {
    this.shiftDropdownConfiguration = this.store.getShiftDropdownSettings();
    this.itemCategoryDropdownConfiguration = this.store.getItemCategoryDropdownSettings();
    this.optionDropdownConfiguration = this.store.getOptionDropdownSettings();
    this.redStatusCategoryDropdownConfiguration = this.store.getRedStatusCategoryDropdownSettings();
    this.redStatusCategoryOptions = this.store.getRedStatusCategoryOptions();
    this.options = this.store.getShiftOptions();
  }

  public get configurations(): FormArray<FormGroup> {
    return this.internalForm.get('configurations') as FormArray<FormGroup>;
  }

  public getShiftGroups(index: number): FormArray<FormGroup> {
    return this.configurations.at(index).get('shifts') as FormArray<FormGroup>;
  }

  public ngOnInit(): void {
    this.store.loadShifts({ limit: 1000 });
    this.store.loadItemCategories({
      fields: ['itemCategory', 'boardId', 'itemType'],
      join: ['itemCategory||name,isDefault,isActive'],
    });

    combineLatest([this.itemCategories$, this.shifts$, this.store.itemCategoriesStatus$, this.store.shiftsStatus$])
      .pipe(
        filter(
          ([, , catStatus, shiftStatus]) => catStatus === ELoadStatus.success && shiftStatus === ELoadStatus.success,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([categories, shifts]) => {
        if (this.formControl.value && this.formControl.value.length > 0) {
          this.initializeForm(this.formControl.value, categories, shifts);
        } else {
          this.addConfiguration();
        }
      });

    this.internalForm.valueChanges.subscribe((): void => {
      this.updateExternalValue();
    });
  }

  private initializeForm(
    data: IItemCategoryShiftConfiguration[],
    categories: IDropdownOption<number>[],
    allShifts: IDropdownOption<number>[],
  ): void {
    this.configurations.clear();

    data.forEach((config: IItemCategoryShiftConfiguration): void => {
      const foundCategory: IDropdownOption<number> | undefined = categories.find(
        (category) => category.id === config.itemCategory,
      );
      const selectedOption: IOptionSelection[] = this.options.filter(
        (option) => option.count === config.shifts.layerCount,
      );

      const configurationGroup: FormGroup = new FormGroup({
        itemCategory: new FormControl(foundCategory ? [foundCategory] : null, [Validators.required]),
        itemCategoryName: new FormControl(config.itemCategoryLabel, [Validators.maxLength(3)]),
        option: new FormControl(selectedOption, [Validators.required]),
        redStatusCategories: new FormControl(this.getSelectedRedStatusCategories(config.redStatusCategories)),
        shifts: new FormArray<FormGroup>([]),
      });

      configurationGroup.get('option')?.valueChanges.subscribe((selection: IOptionSelection[] | null) => {
        const [selectedItem] = selection ?? [];
        this.generateShiftForms(configurationGroup, selectedItem?.count ?? 0);
      });

      const shiftArray = configurationGroup.get('shifts') as FormArray<FormGroup>;

      config.shifts.layers.forEach((layer) => {
        const selectedShifts: IDropdownOption<number>[] = allShifts.filter((shift) => layer.ids.includes(shift.id));

        shiftArray.push(
          new FormGroup({
            shift: new FormControl(selectedShifts.length > 0 ? selectedShifts : null, [Validators.required]),
            shiftName: new FormControl(layer.label, [Validators.maxLength(3)]),
          }),
        );
      });

      this.configurations.push(configurationGroup);
    });
  }

  /**
   * Configurations saved before this option existed have no `redStatusCategories` key at all; an unselected
   * dropdown and a missing key must behave the same, so both map to an empty selection.
   */
  private getSelectedRedStatusCategories(
    savedStatusCategories: TStatusCategory[] | null | undefined,
  ): IDropdownOption<TStatusCategory>[] | null {
    if (!savedStatusCategories?.length) {
      return null;
    }

    return this.redStatusCategoryOptions.filter((option) => savedStatusCategories.includes(option.id));
  }

  public addConfiguration(): void {
    const configurationGroup: FormGroup = new FormGroup({
      itemCategory: new FormControl<IDropdownOption<number>[] | null>(null, [Validators.required]),
      itemCategoryName: new FormControl<string | null>(null, [Validators.maxLength(3)]),
      option: new FormControl<IOptionSelection[] | null>(null, [Validators.required]),
      redStatusCategories: new FormControl<IDropdownOption<TStatusCategory>[] | null>(null),
      shifts: new FormArray<FormGroup>([]),
    });

    configurationGroup.get('option')?.valueChanges.subscribe((selection: IOptionSelection[] | null): void => {
      const [selectedItem]: (IOptionSelection | undefined)[] = selection ?? [];
      this.generateShiftForms(configurationGroup, selectedItem?.count ?? 0);
    });

    this.configurations.push(configurationGroup);
  }

  public removeConfiguration(index: number): void {
    this.configurations.removeAt(index);
  }

  private generateShiftForms(group: FormGroup, count: number): void {
    const shiftArray: FormArray<FormGroup> = group.get('shifts') as FormArray<FormGroup>;
    const currentCount: number = shiftArray.length;

    if (count > currentCount) {
      for (let i: number = currentCount; i < count; i++) {
        shiftArray.push(
          new FormGroup({
            shift: new FormControl<number[] | null>(null, [Validators.required]),
            shiftName: new FormControl<string | null>(null, [Validators.maxLength(3)]),
          }),
        );
      }
    } else if (count < currentCount) {
      while (shiftArray.length > count) {
        shiftArray.removeAt(shiftArray.length - 1);
      }
    }
  }

  private updateExternalValue(): void {
    const rawValue: IInternalFormValue = this.internalForm.getRawValue() as IInternalFormValue;

    if (!rawValue.configurations || rawValue.configurations.length === 0) {
      this.formControl.setValue([]);

      return;
    }

    this.configurations.controls.forEach((control: AbstractControl): void => {
      const configurationGroup: FormGroup = control as FormGroup;
      const categoryNameControl: AbstractControl | null = configurationGroup.get('itemCategoryName');

      if (categoryNameControl?.invalid) {
        categoryNameControl.markAsDirty();
      }

      const shiftArray: FormArray<FormGroup> = configurationGroup.get('shifts') as FormArray<FormGroup>;
      shiftArray.controls.forEach((shiftControl: AbstractControl): void => {
        const shiftGroup: FormGroup = shiftControl as FormGroup;
        const shiftNameControl: AbstractControl | null = shiftGroup.get('shiftName');

        if (shiftNameControl?.invalid) {
          shiftNameControl.markAsDirty();
        }
      });
    });

    const finalPayload: IItemCategoryShiftConfiguration[] = rawValue.configurations.map(
      (config: IConfigurationBlock): IItemCategoryShiftConfiguration => {
        const [selectedOption]: (IOptionSelection | undefined)[] = config.option ?? [];
        const layerCount: number = selectedOption?.count ?? 0;
        const itemCategoryId: number | null =
          config.itemCategory && config.itemCategory.length > 0 ? config.itemCategory[0].id : null;

        const layers: ILayer[] = (config.shifts ?? []).map((shift, index): ILayer => {
          const shiftIds: number[] = Array.isArray(shift.shift)
            ? (shift.shift as unknown as IDropdownOption<number>[]).map((s) => Number(s.id))
            : [];

          return {
            ids: shiftIds,
            label: shift.shiftName,
            layer: index + 1,
          };
        });

        const shiftsPayload: IShiftsPayload = {
          layerCount,
          layers,
        };

        const redStatusCategories: TStatusCategory[] = (config.redStatusCategories ?? []).map(
          (statusCategory) => statusCategory.id,
        );

        return {
          itemCategory: itemCategoryId,
          itemCategoryLabel: config.itemCategoryName,
          redStatusCategories: redStatusCategories.length > 0 ? redStatusCategories : null,
          shifts: shiftsPayload,
        };
      },
    );

    this.formControl.setValue(finalPayload);
    this.syncErrors();
  }

  private syncErrors(): void {
    if (this.internalForm.invalid) {
      this.formControl.setErrors({ incomplete: true });
    } else {
      this.formControl.setErrors(null);
    }

    this.formControl.markAsDirty();
  }
}
